import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { RegularSelectInput } from "components/core";
import { FormikProps } from "formik";
import _ from "lodash";
import { FC } from "react";
import { classNames } from "utils";

export interface ProgramFormProps {
  form: FormikProps<any>;
}

const declineReasonOptions = [
  { value: "NON_ECONOMICAL_PARTICIPATION", label: "Non economical participation" },
  { value: "RATING_REQUIREMENTS_BY_CEDANT", label: "Rating requirements by cedant" },
  { value: "UNFAVORABLE_TOTAL_DEDUCTIONS", label: "Unfavorable total deductions" },
  { value: "UNFAVORABLE_REINSURANCE_TERMS", label: "Unfavorable reinsurance terms" },
  { value: "CLAIMS_PAYMENT_CONCERNS", label: "Claims payment concerns" },
  { value: "LOW_RI_MARKET_CONFIDENCE", label: "Low R/I market confidence" },
];

const ProgramForm: FC<ProgramFormProps> = ({ form }) => {


  const renderInputField = (
    request: any,
    idx: number,
    fieldKey:
      | "withholdingTax"
      | "nicLevy"
      | "agreedBrokerage"
      | "agreedCommission",
    placeholder = "0.00"
  ) => {
    const name = `requests[${idx}].${fieldKey}`;
    const value = request[fieldKey];
    const error = _.get(form.errors, name) as string | undefined;
    const touched = _.get(form.touched, name) as boolean | undefined;

    return (
      <td className="whitespace-nowrap px-3 py-2 text-sm">
        <div className="relative rounded-md shadow-sm">
          <input
            type="number"
            step="0.01"
            name={name}
            id={name}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            value={value}
            placeholder={placeholder}
            className={classNames(
              "block w-full sm:text-sm rounded-md pr-8", // Padding right for the '%' sign
              touched && error
                ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            )}
            aria-describedby={touched && error ? `${name}-error` : undefined}
          />
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-gray-500 sm:text-sm">%</span>
          </div>
        </div>
        {touched && error && (
          <p className="mt-1 text-xs text-red-600" id={`${name}-error`}>
            {error}
          </p>
        )}
      </td>
    );
  };

  return (
    <div className="space-y-6 divide-y divide-gray-200 no-scrollbar overflow-hidden">
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-yellow-400"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Information
            </h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-yellow-700">
              <li>
                If input field for company is left blank, then no with-holidng
                tax and NIC levy will be charged to them. (Note: Reinsurer names are pre-filled in this form.)
              </li>
              <li>
                Leave "Agreed Commission" as <span className="font-bold">0%</span> if
                you want the system to use the initial commission for a
                reinsurer.
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* section to add a reason for decline */}
      <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
        {form.values.rejected_participants?.map((participant: any, index: number) => (
          <div key={participant.offer_participant_id} className="pt-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {participant.reinsurer || "Unnamed Reinsurer"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              If this reinsurer is not accepted, please select the reason.
            </p>

            <div className="mt-4">
              <RegularSelectInput hideLabel label={""} options={[
                ...declineReasonOptions
              ]} {...form} id={`rejected_participants.${index}.reason`} />
              {/* <select
                name={`rejected_participants[${index}].reason`}
                value={form.values.rejected_participants[index].reason}
                onChange={form.handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Select a reason</option>
                {declineReasonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}

              {/* {form?.touched?.rejected_participants?.[index]?.reason &&
                form.errors.rejected_participants?.[index]?.reason && (
                  <div className="text-sm text-red-500 mt-1">
                    {form.errors.rejected_participants[index].reason}
                  </div>
                )} */}
            </div>
          </div>
        ))}
      </div>



      <div className="pt-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Reinsurer Financials
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Specify the percentages for Withholding Tax, NIC Levy, Agreed
          Brokerage, and Agreed Commission for each participating reinsurer.
        </p>
        {form.values.requests && form.values.requests.length > 0 ? (
          <div className="mt-4 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-md">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th
                        scope="col"
                        className="whitespace-nowrap px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Reinsurer
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Withholding Tax (%)
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        NIC Levy (%)
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Agreed Brokerage (%)
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Agreed Commission (%)
                      </th>

                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {form.values.requests?.map((request: any, idx: number) => (
                      <tr
                        key={request.offer_participant_id || `request-${idx}`} // Use a stable unique ID if available
                        className="divide-x divide-gray-200"
                      >
                        <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 bg-gray-50 truncate max-w-xs">
                          {request.reinsurer?.re_company_name || "N/A"}
                        </td>
                        {renderInputField(request, idx, "withholdingTax")}
                        {renderInputField(request, idx, "nicLevy")}
                        {renderInputField(request, idx, "agreedBrokerage")}
                        {renderInputField(request, idx, "agreedCommission")}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-dashed border-gray-300 p-8 text-center">
            <p className="text-sm text-gray-500">
              No reinsurers found for this offer. Please ensure participants are
              correctly assigned to the offer.
            </p>
          </div>
        )}
        {_.get(form.errors, "requests") && !Array.isArray(_.get(form.errors, "requests")) && (
          <p className="mt-2 text-sm text-red-600">
            {_.get(form.errors, "requests") as string}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProgramForm;
